import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { isBoolean, isNumber, isPlainObject, isString } from 'lodash';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { TBaseDataItem } from '../../../types';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  ...baseInputValidationScheme,
  properties: {
    ...baseInputValidationScheme.properties,
    initialValue: {
      type: 'custom',
      validator: (v: unknown) => isString(v) || isBoolean(v) || isNumber(v) || isPlainObject(v),
    },
  },
};

const CustomInputButton: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);

  const {
    name,
    children,
    required,
    validation,
    initialValue,
    formOnly,
    label,
    description,
    placeholder,
  } = tagProps;
  const formContextInput = name ? context.form?.inputs[name] : undefined;
  const [showHelp, setShowHelp] = useState(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-input',
      label,
      description,
      placeholder,
      type: 'button',
      value: initialValue,
      errors: [],
      touched: false,
      required,
      validation,
      initialValue,
      formOnly,
    }),
    [formOnly, initialValue, name, required, validation, label, description, placeholder]
  );

  useEffect(() => {
    if (context.form && !formContextInput) {
      context.form.handleSetInput(name, initialState);
    }
  }, [context.form, formContextInput, initialState, name]);

  const handleClick = useCallback(
    (value: TBaseDataItem) => {
      if (!context.form) return;
      context.form?.handleSetValue(name, value);
    },
    [context.form, name]
  );

  const handleShowHelp = useCallback(() => {
    setShowHelp(prev => !prev);
  }, []);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      handleClick,
      handleShowHelp,
      showHelp,
    }),
    [
      name,
      initialState.type,
      required,
      validation,
      initialValue,
      formContextInput?.errors,
      formContextInput?.touched,
      handleClick,
      handleShowHelp,
      showHelp,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return null;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputButtonContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputButton.displayName = 'CustomInputButton';
export default CustomInputButton;
